// src/components/layout/Footer.jsx
import React from "react";
import {
  Box,
  Grid,
  Typography,
  Stack,
  Link as MUILink,
  IconButton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputBase,
} from "@mui/material";
import TelegramIcon from "@mui/icons-material/Telegram";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/X";

const BG = "#263238";
const BORDER = "rgba(255,255,255,0.08)";
const TEXT = "#EAEFF2";
const MUTED = "#B0BEC5";
const ACCENT = "#F0B90B";

const FooterLink = ({ href = "#", children }) => (
  <MUILink
    href={href}
    underline="none"
    sx={{
      display: "inline-block",
      color: MUTED,
      fontSize: 14,
      py: 0.5,
      "&:hover": { color: TEXT },
    }}
  >
    {children}
  </MUILink>
);

const SocialIcon = ({ href, children, bg = "rgba(255,255,255,0.06)" }) => (
  <IconButton
    href={href}
    target="_blank"
    sx={{
      bgcolor: bg,
      border: `1px solid ${BORDER}`,
      mr: 1,
      "&:hover": {
        bgcolor: "rgba(255,255,255,0.10)",
        boxShadow: `0 0 0 2px rgba(240,185,11,0.18) inset`,
      },
    }}
  >
    {children}
  </IconButton>
);

export default function Footer() {
  const [lang, setLang] = React.useState("en");
  const [ccy, setCcy] = React.useState("USDT");

  return (
    <Box
      component="footer"
      sx={{
        mt: 4,
        borderRadius: 2,
        bgcolor: BG,
        border: `1px solid ${BORDER}`,
        overflow: "hidden",
      }}
    >
      {/* Upper Section */}
      <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
        <Grid container spacing={3}>
          {/* Brand / About */}
          <Grid item xs={12} md={4}>
            <Stack spacing={1.25}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 900,
                  letterSpacing: 0.5,
                  color: TEXT,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <span
                  style={{
                    WebkitTextStroke: "1px #FFFFFF",
                    color: "transparent",
                  }}
                >
                  1
                </span>
                <span>Crypto</span>
                <span
                  style={{
                    WebkitTextStroke: "1px #FFFFFF",
                    color: "transparent",
                  }}
                >
                  X
                </span>
              </Typography>

              <Typography sx={{ color: MUTED, fontSize: 14 }}>
                The Future of AI-Driven Crypto Trading. Fast execution, secure
                wallets, and real-time analytics for every trader.
              </Typography>

              <Stack direction="row" alignItems="center" sx={{ pt: 0.5 }}>
                <SocialIcon href="https://t.me/your_channel">
                  <TelegramIcon sx={{ color: "#229ED9" }} />
                </SocialIcon>
                <SocialIcon href="https://instagram.com/your_profile">
                  <InstagramIcon sx={{ color: "#E4405F" }} />
                </SocialIcon>
                <SocialIcon href="https://x.com/your_handle">
                  <TwitterIcon sx={{ color: TEXT }} />
                </SocialIcon>
              </Stack>
            </Stack>
          </Grid>

          {/* Navigation Columns */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ color: TEXT, fontWeight: 800, mb: 1 }}>
                  Product
                </Typography>
                <Stack>
                  <FooterLink href="/markets">Markets</FooterLink>
                  <FooterLink href="/spot">Spot</FooterLink>
                  <FooterLink href="/futures">Futures</FooterLink>
                  <FooterLink href="/ai-trading">AI Trade</FooterLink>
                </Stack>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Typography sx={{ color: TEXT, fontWeight: 800, mb: 1 }}>
                  Company
                </Typography>
                <Stack>
                  <FooterLink href="/about-us">About</FooterLink>
                  <FooterLink href="/careers">Careers</FooterLink>
                  <FooterLink href="/blog">Blog</FooterLink>
                  <FooterLink href="/news">News</FooterLink>
                </Stack>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Typography sx={{ color: TEXT, fontWeight: 800, mb: 1 }}>
                  Support
                </Typography>
                <Stack>
                  <FooterLink href="/help-center">Help Center</FooterLink>
                  <FooterLink href="/fees">Fees</FooterLink>
                  <FooterLink href="/security">Security</FooterLink>
                  <FooterLink href="/status">System Status</FooterLink>
                </Stack>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Typography sx={{ color: TEXT, fontWeight: 800, mb: 1 }}>
                  Legal
                </Typography>
                <Stack>
                  <FooterLink href="/terms">Terms</FooterLink>
                  <FooterLink href="/privacy">Privacy</FooterLink>
                  <FooterLink href="/disclosure">Disclosure</FooterLink>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ borderColor: BORDER, opacity: 0.7 }} />

      {/* Bottom Section */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 1.25,
        }}
      >
        {/* Legal links */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 0.5, sm: 2 }}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <MUILink
            href="/terms"
            underline="none"
            sx={{ color: ACCENT, fontSize: 13 }}
          >
            Terms
          </MUILink>
          <MUILink
            href="/privacy"
            underline="none"
            sx={{ color: ACCENT, fontSize: 13 }}
          >
            Privacy
          </MUILink>
        </Stack>

        {/* حقوق الملكية */}
        <Typography
          variant="body2"
          sx={{ color: MUTED, textAlign: { xs: "left", md: "right" } }}
        >
          © 2025 1CryptoX — All rights reserved.
        </Typography>
      </Box>

      {/* Disclaimer */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          pb: 2.5,
          pt: 0.5,
          color: MUTED,
          fontSize: 12.5,
        }}
      >
        Cryptocurrency trading carries a high level of risk. Always conduct your
        own research before trading. 1CryptoX is not responsible for any
        financial losses.
      </Box>
    </Box>
  );
}
